import { zodResolver } from "@hookform/resolvers/zod";
import { isAxiosError } from "axios";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { CategoryForm } from "./category-form";

import { FormError, FormModal } from "@/components/kit";

import { Category } from "@/types";
import {
  CATEGORY_FORM_SCHEMA,
  CategoryFormData,
} from "@/schemas/category-form.schema";
import { useEditCategory } from "@/hooks/api/category/useEditCategory";

interface EditCategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  category: Category | null;
}

export function EditCategoryModal({
  isOpen,
  onClose,
  onSuccess,
  category,
}: EditCategoryModalProps) {
  const form = useForm({
    resolver: zodResolver(CATEGORY_FORM_SCHEMA),
    mode: "all",
    defaultValues: {
      id: "",
      image: "",
      name: "",
      nameAr: "",
      isHidden: false,
    },
  });

  const {
    mutate: editCategory,
    isPending: isEditingCategory,
    error,
    isError,
  } = useEditCategory(category?.id!);

  const errorMessage = isError
    ? isAxiosError(error)
      ? error.response?.data?.message
      : "An error occured while creating the category, please try again!"
    : null;

  const handleSubmit = (data: CategoryFormData) => {
    editCategory(data, {
      onSuccess: () => {
        onSuccess();
      },
    });
  };

  useEffect(() => {
    if (category)
      form.reset({
        id: category.id,
        image: category.image,
        name: category.name,
        nameAr: category.nameAr,
        isHidden: category.isHidden,
      });
  }, [category]);

  return (
    <FormModal
      isOpen={isOpen}
      isSubmitting={isEditingCategory}
      submitLabel="Save"
      title="Edit Category"
      onClose={onClose}
      onSubmit={() => form.handleSubmit(handleSubmit)()}
    >
      <FormProvider {...form}>
        <FormError message={errorMessage} />
        <CategoryForm disabledFields={["id"]} />
      </FormProvider>
    </FormModal>
  );
}
